import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useFetchAsanaProjects } from '../hooks/useFetchAsanaProjects';
import { useFetchSlackChannels } from '../hooks/useFetchSlackChannels';
import { useAddNewFormMutation } from '../services/api';
import { validateNewForm } from '../validators';
import { CheckCircle, Send } from 'lucide-react';
import { BasicInfo } from './forms/BasicInfo';
import { FieldPreferences } from './forms/FieldPreferences';
import { StylesPreferences } from './forms/StylesPreferences';
import { errorToast, successToast } from '../utils/toasts';
import { SectionLoader } from './general/Loaders';

export const CreateForm = () => {
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);

  const {
    projects,
    isLoading: asanaProjectsLoading,
    isError: asanaProjectsError,
  } = useFetchAsanaProjects();

  const {
    channels,
    isLoading: slackChannelsLoading,
    isError: slackChannelsError,
  } = useFetchSlackChannels();

  const [addNewForm] = useAddNewFormMutation();

  const createNewForm = values => {
    setSubmitted(true);

    addNewForm({
      ...values,
      asanaProjectId: values?.asanaProject?.value,
      asanaAssigneeId: values?.asanaAssignee?.value || null,
      slackChannelId: values?.slackChannel?.value || null,
    })
      .unwrap()
      .then(_ => {
        successToast('Feedback form created successfully!');
        navigate('/forms');
      })
      .catch(error => {
        console.error(error);
        errorToast('An error occurred while creating the form. Please try again.');
      })
      .finally(() => {
        setSubmitted(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      // basic info about the site and integrations
      siteUrl: '',
      displaySiteUrl: false,
      siteName: '',
      asanaProject: {},
      asanaAssignee: {},
      slackChannel: '',
      subscribers: [],

      // field preferences
      formTitle: '',
      formDescription: '',
      // initializing the form fields with default values to use in to create the asana task
      formFields: [
        {
          id: 'userEmail',
          type: 'email',
          label: 'Please provide your email address:',
          required: true,
        },
        {
          id: 'asanaTaskName',
          type: 'textarea',
          label: 'Please share a short description:',
          required: true,
        },
      ],

      // styles preferences
      formColor: '#00bcd4',
      formFont: 'Open Sans',
      formLogo: '',
    },
    validate: validateNewForm,
    onSubmit: createNewForm,
  });

  if (asanaProjectsLoading || slackChannelsLoading) {
    return (
      <div className="flex flex-col items-center gap-4 my-12">
        <SectionLoader dotColor="slate" />
        <p>Loading...</p>
      </div>
    );
  }

  if (asanaProjectsError) {
    errorToast('Unable to fetch asana projects. Please try again later.');
    return;
  }

  if (slackChannelsError) {
    errorToast('Unable to fetch slack channels. Please try again later.');
    return;
  }

  const breakLineStyle = 'border border-navy w-full my-8';

  return (
    <div className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex justify-center mb-12">
          <h2 className="text-4xl md:text-5xl font-light text-black">New feedback form</h2>
        </div>

        <form onSubmit={formik.handleSubmit} className="flex flex-col items-center mb-20">
          <div className="w-full max-w-lg px-4 mb-10">
            <BasicInfo formik={formik} projects={projects} channels={channels} />

            <hr className={breakLineStyle} />
            <FieldPreferences formik={formik} />

            <hr className={breakLineStyle} />
            <StylesPreferences formik={formik} />
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className="flex items-center bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-4 px-6 rounded-lg transition-all gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitted ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Submitted!
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Create Feedback Form
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
